import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  LinearProgress,
  Paper,
  CircularProgress,
  Dialog,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadingIcon from "@mui/icons-material/Downloading";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import * as XLSX from "xlsx";
import axios from "axios";
import Swal from "sweetalert2";
import Apiurl from "../../Helpers/apiUrl";

const ExtractAllButtonThree = ({ filters, userOpt, totalCount, disabled }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedCount, setExtractedCount] = useState(0);
  const [dynamicTotalCount, setDynamicTotalCount] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState("Calculating...");
  const [startTime, setStartTime] = useState(null);
  const abortControllerRef = useRef(null);

  // Calculate estimated time
  useEffect(() => {
    if (isDownloading && startTime && extractedCount > 0) {
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      const speed = extractedCount / elapsedSeconds;

      if (speed > 0) {
        const remaining = Math.max(0, dynamicTotalCount - extractedCount);
        const secondsRemaining = Math.ceil(remaining / speed);

        if (secondsRemaining < 60) {
          setEstimatedTime(`${secondsRemaining} seconds`);
        } else if (secondsRemaining < 3600) {
          setEstimatedTime(`${Math.ceil(secondsRemaining / 60)} minutes`);
        } else {
          setEstimatedTime(`${Math.ceil(secondsRemaining / 3600)} hours`);
        }
      }
    }
  }, [isDownloading, startTime, extractedCount, dynamicTotalCount]);

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsDownloading(false);
      setProgress(0);
      setExtractedCount(0);
      setDynamicTotalCount(0);
      setEstimatedTime("Calculating...");
      setStartTime(null);

      Swal.fire({
        title: "Export Cancelled",
        text: "The export process has been stopped.",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleExtract = async () => {
    setIsDownloading(true);
    setProgress(0);
    setExtractedCount(0);
    setDynamicTotalCount(0);
    setEstimatedTime("Calculating...");
    setStartTime(Date.now());
    abortControllerRef.current = new AbortController();

    const token = localStorage.getItem("u_token");
    const LIMIT = 2000;
    let allDownloadedData = [];
    let currentPage = 1;
    let keepFetching = true;
    let computedTotalCount = 0;

    try {
      while (keepFetching) {
        const response = await axios.get(
          `${Apiurl}admin/project/view-list`,
          {
            params: {
              page: currentPage,
              limit: LIMIT,
              search: filters.search,
              from: filters.from,
              to: filters.to,
              month: filters.month,
              year: filters.year,
              user: filters.user,
              client: filters.client,
              state: filters.state,
              region: filters.region,
              role: filters.role,
            },
            signal: abortControllerRef.current?.signal,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const pageData = response.data?.data || [];

        if (currentPage === 1) {
          computedTotalCount = response.data?.total || 0;
          setDynamicTotalCount(computedTotalCount);

          if (pageData.length === 0 || computedTotalCount === 0) {
            setIsDownloading(false);
            Swal.fire({
              title: "No Data Available",
              text: "There is no data to export based on current filters.",
              icon: "info",
              confirmButtonColor: "#1565c0",
            });
            return;
          }
        }

        allDownloadedData = [...allDownloadedData, ...pageData];
        setExtractedCount(allDownloadedData.length);

        if (computedTotalCount > 0) {
          const currentProgress = Math.round((allDownloadedData.length / computedTotalCount) * 100);
          setProgress(Math.min(100, currentProgress));
        }

        // Loop breaker check
        if (pageData.length < LIMIT || allDownloadedData.length >= computedTotalCount) {
          if (computedTotalCount === 0) {
            setDynamicTotalCount(allDownloadedData.length);
          }
          setProgress(100);
          keepFetching = false;
        } else {
          currentPage++;
        }
      }

      const EXCEL_HEADERS = [
        "SL No.",
        "Quotation ID",
        "Offer Price",
        "List Price",
        "Client",
        "Client's Region",
        "Client's State",
        "Username",
        "User's Region",
        "User's State",
        "Role",
        "Shared With",
        "Transferred From",
        "PDF | Excel | Whatsapp",
        "Valid Upto",
        "Stage",
        "Created At",
        "Month",
        "Year",
        "Time",
        "Updated At",
        "Update Time",
      ];

      const getRoleName = (role, roleName) => {
        if (roleName) return roleName;
        const roleMap = {
          2: "IC",
          3: "BSP",
          4: "DSP",
          5: "BD",
          6: "Sales Enabler",
          7: "Dealer",
          8: "Studio",
        };
        return roleMap[role] || role || "N/A";
      };

      const getSharedWithText = (shared) => {
        if (!shared) return "Not Shared";
        return shared
          .split(",")
          .map((userId) => {
            const u = userOpt?.find((opt) => String(opt.value) === userId.trim());
            return u ? u.label : userId.trim();
          })
          .join(", ");
      };

      const sheetData = [
        EXCEL_HEADERS,
      ];

      let serialNumber = 1;

      allDownloadedData.forEach((item) => {
        sheetData.push([
          serialNumber++,
          item.project_id || "N/A",
          item.offer_price && Number(item.offer_price) > 0 ? Number(item.offer_price) : Number(item.list_price) || 0,
          item.list_price !== null && item.list_price !== undefined ? Number(item.list_price) : 0,
          item.client_name || "N/A",
          item.client_region || "N/A",
          item.client_state || "N/A",
          item.username || "N/A",
          item.user_region || "N/A",
          item.user_state || "N/A",
          getRoleName(item.role, item.roleName),
          getSharedWithText(item.shared),
          item.transferred_user_name ? item.transferred_user_name : "Not Transferred",
          `${item.pdfCount || 0} | ${item.excelCount || 0} | ${item.whatsCount || 0}`,
          item.valid_date && item.valid_date !== "0000-00-00"
            ? new Date(item.valid_date).toLocaleDateString("en-GB")
            : "N/A",
          item.status || "N/A",
          item.created_at && item.created_at !== "0000-00-00 00:00:00"
            ? new Date(item.created_at).toLocaleDateString("en-GB")
            : "N/A",
          item.created_at && item.created_at !== "0000-00-00 00:00:00"
            ? new Date(item.created_at).toLocaleString("default", { month: "short" })
            : "N/A",
          item.created_at && item.created_at !== "0000-00-00 00:00:00"
            ? new Date(item.created_at).getFullYear()
            : "N/A",
          item.created_at && item.created_at !== "0000-00-00 00:00:00"
            ? new Date(item.created_at).toLocaleTimeString()
            : "N/A",
          item.updated_at && item.updated_at !== "0000-00-00 00:00:00"
            ? new Date(item.updated_at).toLocaleDateString("en-GB")
            : "N/A",
          item.updated_at && item.updated_at !== "0000-00-00 00:00:00"
            ? new Date(item.updated_at).toLocaleTimeString()
            : "N/A",
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      const wscols = [
        { wch: 8 },  // SL No.
        { wch: 22 }, // Quotation ID
        { wch: 15 }, // Offer Price
        { wch: 15 }, // List Price
        { wch: 25 }, // Client
        { wch: 20 }, // Client's Region
        { wch: 20 }, // Client's State
        { wch: 20 }, // Username
        { wch: 20 }, // User's Region
        { wch: 20 }, // User's State
        { wch: 15 }, // Role
        { wch: 25 }, // Shared With
        { wch: 20 }, // Transferred From
        { wch: 22 }, // PDF | Excel | Whatsapp
        { wch: 15 }, // Valid Upto
        { wch: 15 }, // Stage
        { wch: 15 }, // Created At
        { wch: 10 }, // Month
        { wch: 10 }, // Year
        { wch: 15 }, // Time
        { wch: 15 }, // Updated At
        { wch: 15 }, // Update Time
      ];
      ws["!cols"] = wscols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Quotations Data");

      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `Quotation_${timestamp}.xlsx`;
      XLSX.writeFile(wb, fileName);

      Swal.fire({
        title: "Export Successful!",
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Records exported:</strong> ${allDownloadedData.length.toLocaleString()}</p>
            <p><strong>File name:</strong> ${fileName}</p>
          </div>
        `,
        icon: "success",
        confirmButtonColor: "#2e7d32",
        confirmButtonText: "OK",
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        Swal.fire({
          title: "Export Failed",
          text:
            error.message ||
            "Failed to fetch data for export. Please try again.",
          icon: "error",
          confirmButtonColor: "#d32f2f",
        });
      }
    } finally {
      setIsDownloading(false);
      setProgress(0);
      setEstimatedTime("Calculating...");
      setStartTime(null);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={isDownloading ? <DownloadingIcon /> : <TableChartIcon />}
        onClick={handleExtract}
        disabled={isDownloading || disabled}
        sx={{
          textTransform: "none",
          bgcolor: isDownloading || disabled ? "grey.600" : "#1565c0",
          "&:hover": {
            bgcolor: isDownloading || disabled ? "grey.600" : "#0d47a1",
            boxShadow: "0 4px 12px rgba(21, 101, 192, 0.3)",
          },
          py: '2px',
          px: '14px',
          borderRadius: 12,
          fontSize: "12px",
          transition: "all 0.2s ease-in-out",
          boxShadow: "0 2px 8px rgba(21, 101, 192, 0.2)",
          position: "relative",
          overflow: "hidden",
          "&::before": isDownloading
            ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
              animation: "shimmer 2s infinite",
            }
            : {},
        }}
      >
        {isDownloading ? (
          <>
            <CircularProgress size={12} sx={{ mr: 1, color: "white" }} />
            Exporting ({progress}%)
          </>
        ) : (
          `Dump`
        )}
      </Button>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>

      <Dialog
        open={isDownloading}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            width: 420,
            p: 0,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#04121f",
            p: 3,
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <DownloadingIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exporting Report
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Please wait while we prepare your file
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, bgcolor: "white" }}>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Export Progress
              </Typography>
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: 700 }}
              >
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: "grey.100",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  background: "linear-gradient(90deg, #1565c0, #2196f3)",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 3,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Records Processed
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FileDownloadDoneIcon
                  color="success"
                  sx={{ fontSize: 20 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {extractedCount.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                of {dynamicTotalCount.toLocaleString()} total
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Time Remaining
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#ed6c02" }}
              >
                {estimatedTime}
              </Typography>
            </Paper>
          </Box>

          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleCancel}
            fullWidth
            sx={{
              textTransform: "none",
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
                bgcolor: "error.light",
                color: "error.dark",
              },
            }}
          >
            Cancel Export
          </Button>

          <Typography
            variant="caption"
            color="error.main"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 2,
              fontWeight: 500,
              bgcolor: "error.50",
              p: 1,
              borderRadius: 1,
            }}
          >
            ⚠️ Do not close this window while export is in progress
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default ExtractAllButtonThree;